/**
 * PlanComplianceGate Component
 * 
 * Gate global que verifica conformidade do plano após downgrade.
 * Bloqueia uso do app se o usuário possui mais membros do que o limite do plano atual.
 * 
 * ESTRATÉGIA ZERO-REALTIME:
 * - NÃO usa useSubscription() (para evitar abrir mais um canal Realtime)
 * - Chama RPC can_use_feature('family_member') diretamente
 * - Cache TTL de 60s para evitar chamadas repetidas
 * - Verifica apenas em momentos-chave:
 *   1. Mount inicial do App
 *   2. Navegação para /dashboard (com cache)
 *   3. Após fechar modal "Gerenciar Família" (revalidação única)
 * 
 * CONSIDERAÇÕES:
 * - Durante 'pending_downgrade' e antes de active_until, usuário mantém acesso ao plano maior (não bloqueia)
 * - Após virada (status='active' no plano menor), verifica se membros > limite do novo plano
 * 
 * EVOLUÇÃO FUTURA:
 * - Após implementar SubscriptionProvider centralizado (1 canal Realtime por usuário),
 *   este componente pode consumir o contexto sem abrir novos canais.
 * - Ver: Docs/INCIDENTE_2026-01-29_AUMENTO_EGRESS_SUPABASE.md (seção 11)
 * 
 * Fase 6 - Bloqueio Pós-Downgrade
 */

import { useEffect, useState, useRef } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { flushSync } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { PlanComplianceModal } from './PlanComplianceModal';
import { logger } from '../../utils/logger';

// Chave para persistir "navegação permitida" entre reloads
const NAV_FLAG_KEY = 'pcg:allow_navigation';

interface PlanComplianceGateProps {
  children: ReactNode;
}

interface ComplianceCheck {
  isCompliant: boolean;
  currentMembers: number;
  maxMembers: number;
  currentPlan: 'free' | 'basic' | 'premium';
  status: string;
}

// Cache global para evitar chamadas repetidas
const complianceCache = {
  lastCheck: 0,
  result: null as ComplianceCheck | null,
  TTL_MS: 60000 // 60 segundos
};

export function PlanComplianceGate({ children }: PlanComplianceGateProps) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [compliance, setCompliance] = useState<ComplianceCheck | null>(null);
  const [allowNavigation, setAllowNavigation] = useState(false);
  const lastNavRef = useRef<{ path: string; ts: number }>({ path: '', ts: 0 });

  /**
   * Verifica conformidade usando RPC can_use_feature('family_member')
   * - Sem Realtime: apenas 1 chamada RPC por verificação
   * - Cache TTL: evita chamadas repetidas em 60s
   */
  async function checkCompliance(forceRefresh = false): Promise<ComplianceCheck | null> {
    if (!user) return null;

    // Cache hit (TTL válido)
    const now = Date.now();
    if (!forceRefresh && complianceCache.result && (now - complianceCache.lastCheck) < complianceCache.TTL_MS) {
      logger.debug('[PlanComplianceGate] Cache hit, usando resultado anterior');
      return complianceCache.result;
    }

    try {
      // 1. Buscar assinatura atual (status + plano)
      const { data: subData, error: subError } = await supabase
        .from('user_subscriptions')
        .select('plan_tier, status, active_until, pending_downgrade_to, pending_downgrade_at')
        .eq('user_id', user.id)
        .single();

      if (subError || !subData) {
        logger.warn('[PlanComplianceGate] Usuário sem assinatura, assumindo free');
        // Sem assinatura = free (não bloqueia)
        const freeCompliance: ComplianceCheck = {
          isCompliant: true,
          currentMembers: 0,
          maxMembers: 3,
          currentPlan: 'free',
          status: 'free'
        };
        complianceCache.lastCheck = now;
        complianceCache.result = freeCompliance;
        return freeCompliance;
      }

      // 2. Durante pending_downgrade, mantém acesso ao plano atual (não ao pending_downgrade_to)
      const isActive = subData.status === 'active' || subData.status === 'grace_period';
      const isPendingDowngrade = subData.status === 'pending_downgrade';
      const activeUntil = new Date(subData.active_until);
      const nowDate = new Date();

      // Se pending_downgrade e ainda dentro do ciclo, não bloqueia (usuário ainda tem acesso ao plano maior)
      if (isPendingDowngrade && nowDate < activeUntil) {
        logger.debug('[PlanComplianceGate] Status pending_downgrade, mantém acesso ao plano atual até', activeUntil);
        const pendingCompliance: ComplianceCheck = {
          isCompliant: true,
          currentMembers: 0,
          maxMembers: 999,
          currentPlan: subData.plan_tier as 'free' | 'basic' | 'premium',
          status: subData.status
        };
        complianceCache.lastCheck = now;
        complianceCache.result = pendingCompliance;
        return pendingCompliance;
      }

      // 3. Se não está ativo, não bloqueia (outro fluxo deve tratar)
      if (!isActive && !isPendingDowngrade) {
        logger.debug('[PlanComplianceGate] Status não ativo:', subData.status);
        const inactiveCompliance: ComplianceCheck = {
          isCompliant: true,
          currentMembers: 0,
          maxMembers: 999,
          currentPlan: subData.plan_tier as 'free' | 'basic' | 'premium',
          status: subData.status
        };
        complianceCache.lastCheck = now;
        complianceCache.result = inactiveCompliance;
        return inactiveCompliance;
      }

      // 4. Chamar RPC can_use_feature('family_member') para obter limites e uso
      const { data: featureData, error: featureError } = await supabase.rpc('can_use_feature', {
        p_feature_key: 'family_member',
        p_user_id: user.id
      });

      if (featureError) {
        logger.error('[PlanComplianceGate] Erro ao verificar feature:', featureError);
        return null;
      }

      // Normalizar resposta (pode ser array ou objeto)
      const raw = (Array.isArray(featureData) ? (featureData?.[0] || {}) : (featureData || {})) as any;
      const usageCount = Number(raw.usage_count ?? 0);
      const limitValue = raw.limit_value;
      const isUnlimited = String(limitValue).toLowerCase() === 'unlimited';
      const limit = isUnlimited ? 999 : Number.parseInt(String(limitValue ?? '3'), 10);

      logger.debug('[PlanComplianceGate] Verificação:', {
        plan: subData.plan_tier,
        status: subData.status,
        members: usageCount,
        limit,
        isUnlimited
      });

      // 5. Determinar conformidade
      // CRITÉRIO: bloqueia se usageCount > limit (excedeu o limite)
      // Se usageCount == limit, não bloqueia (apenas impede adicionar mais, não bloqueia uso)
      const isCompliant = usageCount <= limit;

      const result: ComplianceCheck = {
        isCompliant,
        currentMembers: usageCount,
        maxMembers: limit,
        currentPlan: subData.plan_tier as 'free' | 'basic' | 'premium',
        status: subData.status
      };

      // Atualizar cache
      complianceCache.lastCheck = now;
      complianceCache.result = result;

      return result;
    } catch (error) {
      logger.error('[PlanComplianceGate] Erro ao verificar conformidade:', error);
      return null;
    }
  }

  /**
   * Verificação inicial ao montar o gate
   */
  useEffect(() => {
    if (!user) return;

    async function initialCheck() {
      const result = await checkCompliance(true); // força refresh no primeiro check
      if (result) {
        setCompliance(result);
        // Só abre modal se a rota atual for protegida
        const inAllowed = location.pathname.startsWith('/profile') || location.pathname.startsWith('/payment');
        const isProtected = location.pathname === '/dashboard' || location.pathname.startsWith('/member');
        const shouldShow = isProtected && !inAllowed && !allowNavigation && !result.isCompliant;
        setShowModal(shouldShow);
      }
    }

    initialCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  /**
   * Verificação ao navegar para dashboard (com cache TTL)
   */
  useEffect(() => {
    // Checar apenas quando o path muda para dashboard; evitar depender de `user` (objeto) para não disparar em loop
    if (!user) return;

    const isDashboard = location.pathname === '/dashboard';
    if (!isDashboard) return;

    // Detecta se houve navegação permitida persistida (ex.: usuário tentou burlar e voltou)
    const persistedAllow = (() => { try { return sessionStorage.getItem(NAV_FLAG_KEY) === '1'; } catch { return false; } })();

    // Se estamos no dashboard mas acabamos de acionar uma navegação permitida,
    // não executar verificação aqui para evitar reabrir o modal antes da rota mudar
    if (allowNavigation || persistedAllow) {
      logger.debug('[PlanComplianceGate] Dashboard: navegação permitida ativa, pulando verificação');
      return;
    }

    const now = Date.now();
    const isCacheValid = !!(complianceCache.result && (now - complianceCache.lastCheck) < complianceCache.TTL_MS);

    // Throttle: evitar múltiplas execuções em sequência para o mesmo path
    const samePathRapid = lastNavRef.current.path === location.pathname && (now - lastNavRef.current.ts) < 300;
    if (samePathRapid) return;

    // Se já temos estado local em conformidade com cache válido, não reconsultar
    if (isCacheValid && compliance) return;

    lastNavRef.current = { path: location.pathname, ts: now };
    logger.debug('[PlanComplianceGate] Navegou para dashboard, verificando conformidade (com cache)');
    checkCompliance(persistedAllow).then(result => {
      if (!result) return;
      // Evitar setState desnecessário: só atualiza quando mudar
      setCompliance(prev => {
        const changed = !prev || prev.isCompliant !== result.isCompliant || prev.currentMembers !== result.currentMembers || prev.maxMembers !== result.maxMembers || prev.currentPlan !== result.currentPlan || prev.status !== result.status;
        if (changed) {
          return result;
        }
        return prev;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, allowNavigation]);

  // Removido o fallback para evitar piscar; reabertura agora ocorre apenas na revalidação pós-navegação

  /**
   * Sincroniza o estado showModal com as condições de bloqueio (com delay para evitar piscar)
   * Garante reabertura automática ao entrar em rota protegida enquanto não conforme
   */
  useEffect(() => {
    if (!user) return;
    if (!compliance) return;
    const inAllowed = location.pathname.startsWith('/profile') || location.pathname.startsWith('/payment');
    const isProtected = location.pathname === '/dashboard' || location.pathname.startsWith('/member');
    const shouldShow = isProtected && !inAllowed && !compliance.isCompliant;

    // Se deveria mostrar mas está em navegação permitida, aguardar um pouco para evitar piscar
    if (shouldShow && allowNavigation) {
      const timer = setTimeout(() => {
        logger.debug('[PlanComplianceGate] Reabrindo modal após navegação permitida (delay 400ms)...');
        setShowModal(true);
        setAllowNavigation(false);
        try { sessionStorage.removeItem(NAV_FLAG_KEY); } catch { /* ignore */ }
      }, 400);
      return () => clearTimeout(timer);
    }

    // Caso contrário, aplicar imediatamente
    setShowModal(shouldShow);
    if (!shouldShow && allowNavigation) {
      setAllowNavigation(false);
      try { sessionStorage.removeItem(NAV_FLAG_KEY); } catch { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, allowNavigation, compliance?.isCompliant, user?.id]);

  /**
   * Handler para quando usuário fecha modal "Gerenciar Família"
   * Fecha modal temporariamente e permite navegação
   */
  function handleManageFamily() {
    logger.debug('[PlanComplianceGate] Usuário foi gerenciar família');
    // Fecha modal e permite navegação temporariamente (sincronamente)
    flushSync(() => {
      setShowModal(false);
      setAllowNavigation(true);
      try { sessionStorage.setItem(NAV_FLAG_KEY, '1'); } catch { /* ignore */ }
    });
    navigate('/profile?tab=family');
  }

  /**
   * Handler para quando usuário escolhe upgrade
   */
  function handleUpgrade() {
    logger.debug('[PlanComplianceGate] Usuário foi fazer upgrade');
    // Fecha modal e permite navegação temporariamente (sincronamente)
    flushSync(() => {
      setShowModal(false);
      setAllowNavigation(true);
      try { sessionStorage.setItem(NAV_FLAG_KEY, '1'); } catch { /* ignore */ }
    });
    navigate('/payment');
  }

  // Renderizar modal se não conforme
  return (
    <>
      {(() => {
        const inAllowed = location.pathname.startsWith('/profile') || location.pathname.startsWith('/payment');
        const isProtected = location.pathname === '/dashboard' || location.pathname.startsWith('/member');
        const computedShouldShow = !!compliance && !compliance.isCompliant && !allowNavigation && !inAllowed && isProtected;
        const visible = (showModal || computedShouldShow) && !!compliance;
        return visible;
      })() && compliance && (
        <PlanComplianceModal
          currentMembers={compliance.currentMembers}
          maxMembers={compliance.maxMembers}
          currentPlan={compliance.currentPlan}
          onManageFamily={handleManageFamily}
          onUpgrade={handleUpgrade}
        />
      )}
      {children}
    </>
  );
}
